import React, { useState, useEffect, useRef } from 'react';
import styles from './TypewriterTitle.module.css';

const TypewriterTitle = ({ fullTitle, startTyping, speed = 50 }) => {
  const [displayedTitle, setDisplayedTitle] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const indexRef = useRef(0);
  const timeoutRef = useRef(null);
  const currentTextRef = useRef('');

  useEffect(() => {
    // Only start typing if the startTyping signal is true AND we have a title
    if (startTyping && fullTitle) {
 
       // Clear any existing timeout
       if (timeoutRef.current) clearTimeout(timeoutRef.current);
 
       // Reset state for typing
       currentTextRef.current = '';
       indexRef.current = 0;
       setIsTyping(true);
       setDisplayedTitle('');

       const typeCharacter = () => {
         if (indexRef.current < fullTitle.length) {
           currentTextRef.current = fullTitle.substring(0, indexRef.current + 1);
           setDisplayedTitle(currentTextRef.current);
           indexRef.current += 1;
           
           timeoutRef.current = setTimeout(typeCharacter, speed);
         } else {
           setIsTyping(false);
           currentTextRef.current = fullTitle;
           setDisplayedTitle(fullTitle);
         }
       };

       // Start the typing process immediately if title has characters
       // Start typing after a brief delay to ensure state is cleared
       if (fullTitle.length > 0) {
         timeoutRef.current = setTimeout(typeCharacter, 16);
       } else {
         setIsTyping(false);
       }
     } else if (!startTyping && displayedTitle !== fullTitle) {
        // If the startTyping signal becomes false (e.g., timeout in App.js clears the ID),
        // ensure the full title is displayed immediately if it wasn't already.
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setDisplayedTitle(fullTitle || ''); // Display full title or empty string if no title
        setIsTyping(false);
    }

    // Cleanup function to clear timeout if component unmounts or props change mid-typing
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [startTyping, fullTitle, speed]);
  
  // Effect to handle cases where the title prop changes *without* startTyping being true
  // (e.g., initial load or manual rename). Ensures the correct full title is shown.
  useEffect(() => {
    if (!startTyping) {
        setDisplayedTitle(fullTitle || '');
        setIsTyping(false); // Ensure typing state is false
        if (timeoutRef.current) clearTimeout(timeoutRef.current); // Clear any lingering timeouts
    }
  }, [fullTitle]); // Only run when fullTitle changes

  return (
    <span className={styles.typewriterContainer}>
      {displayedTitle}
      {isTyping && <span className={styles.cursor}>|</span>}
    </span>
  );
};

export default TypewriterTitle;
